import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, User as UserIcon } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";

const navItems = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/what-is-fnd", label: "What is FND", testid: "nav-what-is-fnd" },
  { to: "/symptoms", label: "Symptoms", testid: "nav-symptoms" },
  { to: "/treatments", label: "Treatments", testid: "nav-treatments" },
  { to: "/for-kids", label: "For Kids", testid: "nav-for-kids" },
  { to: "/my-story", label: "My Story", testid: "nav-my-story" },
  { to: "/community", label: "Community", testid: "nav-community" },
  { to: "/resources", label: "Resources", testid: "nav-resources" },
  { to: "/support", label: "Support", testid: "nav-support" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-bone/85 dark:bg-charcoal/85 backdrop-blur-md border-b border-bone-200 dark:border-charcoal-light/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20 gap-4">
          <Link
            to="/"
            data-testid="logo-link"
            className="group flex items-center gap-3 shrink-0"
          >
            <span className="relative inline-flex items-center justify-center w-12 h-12 rounded-full bg-terracotta/10 ring-1 ring-terracotta/25 overflow-hidden transition-transform duration-500 group-hover:scale-105">
              <img
                src="/logo-mark.png"
                alt="Quiet Signals mark"
                className="w-11 h-11 object-cover scale-110"
                loading="eager"
              />
            </span>
            <div className="flex flex-col leading-none">
              <span className="font-serif text-xl text-charcoal dark:text-bone tracking-tight">
                Quiet Signals
              </span>
              <span className="text-[0.65rem] uppercase tracking-[0.22em] text-charcoal-light dark:text-bone/60 mt-0.5">
                Understanding FND
              </span>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5" data-testid="primary-nav">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `relative px-2.5 py-2.5 text-sm font-medium tracking-wide transition-colors duration-300 whitespace-nowrap ${
                    isActive
                      ? "text-terracotta"
                      : "text-charcoal dark:text-bone hover:text-terracotta dark:hover:text-terracotta"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{item.label}</span>
                    <span
                      className={`absolute left-2.5 right-2.5 -bottom-0.5 h-px bg-terracotta origin-left transition-transform duration-500 ${
                        isActive ? "scale-x-100" : "scale-x-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {user ? (
              <Link
                to="/community"
                data-testid="header-user-link"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-terracotta/30 text-terracotta hover:bg-terracotta hover:text-bone transition text-xs font-medium"
              >
                <UserIcon size={14} strokeWidth={1.5} />
                {user.first_name}
              </Link>
            ) : (
              <Link
                to="/signin"
                data-testid="header-signin-link"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-charcoal/20 dark:border-bone/30 text-charcoal dark:text-bone hover:border-terracotta hover:text-terracotta transition text-xs font-medium"
              >
                Sign in
              </Link>
            )}
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setOpen((s) => !s)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
              className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-bone-200 dark:border-charcoal-light/40 text-charcoal dark:text-bone hover:bg-bone-100 dark:hover:bg-charcoal-light/20 transition"
            >
              {open ? <X size={18} strokeWidth={1.5} /> : <Menu size={18} strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-nav"
            className="xl:hidden pb-6 pt-2 flex flex-col gap-1 animate-fade-in"
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`${item.testid}-mobile`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base font-medium transition ${
                    isActive
                      ? "bg-terracotta/10 text-terracotta"
                      : "text-charcoal dark:text-bone hover:bg-bone-100 dark:hover:bg-charcoal-light/20"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <NavLink
              to={user ? "/community" : "/signin"}
              onClick={() => setOpen(false)}
              data-testid="mobile-auth-link"
              className="mt-2 px-4 py-3 rounded-xl text-base font-medium bg-terracotta/10 text-terracotta hover:bg-terracotta/20 transition"
            >
              {user ? `Hello, ${user.first_name}` : "Sign in"}
            </NavLink>
          </div>
        )}
      </div>
    </header>
  );
}
